import { useEffect, useState } from 'react'
import { motion } from 'motion/react'
import {
  Cpu, Wifi, Shield, Globe, Clock,
  Zap, Gamepad2, Layers, Package, Users, HardDrive,
  Sparkles,
} from 'lucide-react'
import { MarketingLayout } from '../components/MarketingLayout'
import { SEO } from '../components/SEO'
import { CTABanner } from '../components/CTABanner'
import { FAQ } from '../components/FAQ'
import { PlansSection } from '../components/PlansSection'
import { FeatureGrid } from '../components/FeatureCard'
import { type Plan, type PlansResponse } from '../types'

const APP_NAME = import.meta.env.VITE_APP_NAME || 'Voltary'
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || ''

const features = [
  {
    icon: <Cpu size={22} />,
    title: 'CPU dedicado',
    desc: 'Cada servidor corre en su propio thread. Sin vecinos robando recursos.',
  },
  {
    icon: <HardDrive size={22} />,
    title: 'SSD NVMe',
    desc: 'Carga de chunks y world saves casi instantáneos. Tu TPS se mantiene estable.',
  },
  {
    icon: <Wifi size={22} />,
    title: 'Latencia baja',
    desc: 'Servidores en Europa y América para que juegues sin lag estés donde estés.',
  },
  {
    icon: <Layers size={22} />,
    title: 'Cualquier versión',
    desc: 'Java Edition: cualquier versión oficial desde 1.8 en adelante. Forge, Fabric, Paper, Spigot, Bukkit.',
  },
  {
    icon: <Package size={22} />,
    title: 'Modpacks',
    desc: 'Sube modpacks por FTP a la carpeta /mods. Compatible con CurseForge, Modrinth y packs personalizados.',
  },
  {
    icon: <Package size={22} />,
    title: 'Plugins/Mods',
    desc: 'Instala tus plugins y mods favoritos desde el panel web',
  },
]

const softwares = [
  {
    name: 'Vanilla',
    desc: 'La experiencia original de Minecraft sin modificaciones. La opción ideal para partidas tradicionales y servers privados entre amigos.',
    icon: <Gamepad2 size={20} />,
  },
  {
    name: 'Spigot',
    desc: 'Servidor optimizado con soporte para plugins. Perfecto para añadir funciones personalizadas manteniendo la estabilidad.',
    icon: <Package size={20} />,
  },
  {
    name: 'Paper',
    desc: 'Fork de Spigot diseñado para ofrecer el máximo rendimiento y corregir fallos. La elección estándar para servidores fluidos y sin lag.',
    icon: <Zap size={20} />,
  },
  {
    name: 'Fabric',
    desc: 'Motor de mods liviano, moderno y modular. Excelente para cargas rápidas, excelente rendimiento y compatibilidad con mods actualizados.',
    icon: <Zap size={20} />,
  },
  {
    name: 'Forge',
    desc: 'La plataforma líder para modificar Minecraft. La mejor opción si buscas instalar modpacks complejos y personalizaciones avanzadas.',
    icon: <Zap size={20} />,
  },
  {
    name: 'Próximamente',
    desc: 'Estamos trabajando para añadir más motores y plataformas compatibles muy pronto.',
    icon: <Sparkles size={20} />
  },
]

const faqs = [
  {
    q: '¿Cuánta RAM necesito para un servidor de Minecraft?',
    a: 'Para vanilla con 5 amigos, 2-4 GB bastan. Para 15+ jugadores con plugins, 6-8 GB. Para mods pesados como RLCraft o All The Mods, 10-12 GB mínimo. Si dudas, empieza con el plan medio y sube cuando notes TPS bajo.',
  },
  {
    q: '¿Qué versión de Minecraft soportáis?',
    a: 'Cualquier versión oficial de Java Edition desde 1.8 en adelante. No soportamos snapshots ni versiones beta. Puedes cambiar la versión del servidor desde el panel cuando quieras.',
  },
  {
    q: '¿Puedo instalar modpacks?',
    a: 'Sí, pero solo por acceso FTP. Sube el modpack (archivo .zip descomprimido) a la carpeta /mods. El panel web permite instalar mods individuales pero modpacks completos aún no. Compatible con CurseForge, Modrinth y packs personalizados.',
  },
  {
    q: '¿Puedo migrar mi servidor desde Aternos o Minehut?',
    a: 'Sí. Descarga la carpeta del mundo desde tu proveedor actual, sube los archivos a Voltary por FTP o panel web, y en 5 minutos tienes el mismo mundo corriendo en hardware dedicado.',
  },
]

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `Hosting de Minecraft Java - ${APP_NAME}`,
    description: 'Hosting de servidores de Minecraft Java Edition con hardware dedicado. Soporta Vanilla, Forge, Fabric, Spigot, Paper y modpacks populares.',
    provider: { '@type': 'Organization', name: APP_NAME },
    areaServed: { '@type': 'Place', name: 'España, Latinoamérica' },
    serviceType: 'Game Server Hosting',
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'USD',
      lowPrice: 1,
      highPrice: 50,
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Inicio', item: 'https://voltary.org/' },
      { '@type': 'ListItem', position: 2, name: 'Minecraft', item: 'https://voltary.org/minecraft' },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  },
]

interface MinecraftProps {
  initialPlans?: Plan[]
}

export function Minecraft({ initialPlans = [] }: MinecraftProps) {
  const [plans, setPlans] = useState<Plan[]>(initialPlans)

  useEffect(() => {
    let cancelled = false
    fetch(`${BACKEND_URL}/api/servers/plans/${encodeURIComponent('Minecraft')}`)
      .then((r) => r.json() as Promise<PlansResponse>)
      .then((data) => {
        if (!cancelled && Array.isArray(data.plans)) {
          setPlans(data.plans)
        }
      })
      .catch(() => {/* silencioso: mantenemos planes iniciales */})
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <MarketingLayout>
      <SEO
        title={`Hosting de Minecraft Java - ${APP_NAME} | Servidores dedicados`}
        description="Hosting de servidores de Minecraft Java con hardware dedicado. Soporta Vanilla, Forge, Fabric, Spigot, Paper y modpacks. Setup en 2 minutos, paga por horas."
        canonical="https://voltary.org/minecraft"
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="minecraft-hero">
        <div className="minecraft-hero-bg" aria-hidden="true">
          <div className="minecraft-hero-glow minecraft-hero-glow--1" />
          <div className="minecraft-hero-glow minecraft-hero-glow--2" />
        </div>

        <motion.div
          className="minecraft-hero-content"
          initial="hidden"
          animate="show"
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } },
          }}
        >
          <motion.span
            className="minecraft-hero-eyebrow"
            variants={{
              hidden: { opacity: 0, y: 16, scale: 0.85 },
              show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5 } },
            }}
          >
            <Gamepad2 size={14} /> Hosting Minecraft Java
          </motion.span>

          <motion.h1
            className="minecraft-hero-title"
            variants={{
              hidden: { opacity: 0, y: 40 },
              show: { opacity: 1, y: 0, transition: { duration: 0.8 } },
            }}
          >
            Servidores de Minecraft<br />
            <span className="minecraft-hero-title-accent">sin colas, sin lag</span>
          </motion.h1>

          <motion.p
            className="minecraft-hero-subtitle"
            variants={{
              hidden: { opacity: 0, y: 30 },
              show: { opacity: 1, y: 0, transition: { duration: 0.7 } },
            }}
          >
            Hardware dedicado para tu servidor. Cualquier versión, cualquier modpack.
            Configura en 2 minutos y paga solo por las horas que juegas.
          </motion.p>

          <motion.div
            className="minecraft-hero-cta"
            variants={{
              hidden: { opacity: 0, y: 20 },
              show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
            }}
          >
            <motion.a
              href="/login"
              className="minecraft-hero-primary"
              whileHover={{ y: -3, scale: 1.02 }}
              whileTap={{ scale: 0.96 }}
            >
              Crear mi servidor Minecraft
            </motion.a>
            <motion.a
              href="#features"
              className="minecraft-hero-secondary"
              whileHover={{ y: -3 }}
              whileTap={{ scale: 0.96 }}
            >
              Ver características
            </motion.a>
          </motion.div>

          <motion.div
            className="minecraft-hero-trust"
            variants={{
              hidden: { opacity: 0 },
              show: { opacity: 1, transition: { duration: 0.8, delay: 0.3 } },
            }}
          >
            <span><Users size={14} /> Soporta hasta 50 jugadores</span>
            <span><Clock size={14} /> Setup en 2 minutos</span>
            <span><Wifi size={14} /> 99.9% uptime</span>
          </motion.div>
        </motion.div>
      </section>

      {/* FEATURES */}
      <section id="features" className="minecraft-section">
        <div className="minecraft-section-header">
          <span className="minecraft-section-tag">CARACTERÍSTICAS</span>
          <h2>Todo lo que tu servidor de Minecraft necesita</h2>
          <p>
            Hardware pensado para que tu TPS se mantenga alto incluso con
            30 jugadores explorando mundos gigantes.
          </p>
        </div>

        <FeatureGrid
          items={features.map((f) => ({
            icon: f.icon,
            title: f.title,
            description: f.desc,
          }))}
        />
      </section>

      {/* SOFTWARES */}
      <section className="minecraft-section minecraft-section--alt">
        <div className="minecraft-section-header">
          <span className="minecraft-section-tag">Software de Minecraft</span>
          <h2>Soporte para diferentes software de Minecraft Java</h2>
        </div>

        <div className="minecraft-modes-grid">
          {softwares.map((mode, idx) => (
            <motion.div
              key={mode.name}
              className="minecraft-mode-card"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.4, delay: idx * 0.06 }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.5rem' }}>
                <div className="minecraft-mode-icon">{mode.icon}</div>
                <h3>{mode.name}</h3>
              </span>
              <p>{mode.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PLANS */}
      <PlansSection
        plans={plans}
        title="Elige tu plan"
        description="Tres tiers según el tamaño de tu comunidad. Cada plan disponible en Europa y América."
      />

      {/* FAQ */}
      <FAQ items={faqs} title="Preguntas frecuentes sobre hosting de Minecraft" />

      {/* CTA */}
      <CTABanner
        title="Tu servidor de Minecraft"
        primaryLabel="Crear servidor"
        primaryHref="/login"
      />
    </MarketingLayout>
  )
}
